import { useState, useCallback } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import type { Panel as DashboardPanel, DashboardLayout } from '@/types/dashboard';
import { t } from '@/i18n';
import { EditorPreview } from './EditorPreview';
import { EditorData } from './EditorData';
import { EditorSettings } from './EditorSettings';
import type { ChartConfig, ChartSeriesConfig } from '@/types/chart';
import './PanelEditor.css';

interface PanelEditorProps {
  panel: DashboardPanel;
  contextKey: string;
  layout: DashboardLayout;
  onApply: (updatedPanel: DashboardPanel) => void;
  onCancel: () => void;
  sourceKey: string;
}

export function PanelEditor({ panel, contextKey, layout, onApply, onCancel, sourceKey }: PanelEditorProps) {
  const [localPanel, setLocalPanel] = useState<DashboardPanel>(() => ({ ...panel }));
  const initialSnapshot = JSON.stringify(panel);
  const isDirty = JSON.stringify(localPanel) !== initialSnapshot;

  const handleFormulasChange = useCallback((formulas: Record<string, string>) => {
    setLocalPanel(prev => {
      if (prev.type === 'indicator' || prev.type === 'labeled_list' || prev.type === 'entity_list') {
        return { ...prev, data: { ...prev.data, formulas } };
      }
      if (prev.type === 'chart') {
        const config = prev.config as unknown as ChartConfig;
        const validFieldNames = new Set((config.series ?? []).map((s: ChartSeriesConfig) => s.fieldName));
        const existingFormulas = prev.data && 'formulas' in prev.data
          ? (prev.data as { formulas: Record<string, string> }).formulas
          : {};
        const filteredFormulas = Object.fromEntries(
          Object.entries({ ...existingFormulas, ...formulas })
            .filter(([key]) => validFieldNames.has(key))
        );
        return { ...prev, data: { ...prev.data, formulas: filteredFormulas } };
      }
      return prev;
    });
  }, []);

  const handleConfigChange = useCallback((config: Record<string, unknown>) => {
    setLocalPanel(prev => {
      if (prev.type === 'chart') {
        const chartConfig = config as unknown as ChartConfig;
        const validFields = new Set((chartConfig.series ?? []).map((s: ChartSeriesConfig) => s.fieldName));
        const existingFormulas = prev.data && 'formulas' in prev.data
          ? (prev.data as { formulas: Record<string, string> }).formulas
          : {};
        const cleanedFormulas = Object.fromEntries(
          Object.entries(existingFormulas).filter(([k]) => validFields.has(k))
        );
        return { ...prev, config, data: { ...prev.data, formulas: cleanedFormulas } };
      }
      return { ...prev, config };
    });
  }, []);

  const handleTitleChange = useCallback((title: string) => {
    setLocalPanel(prev => ({ ...prev, title }));
  }, []);

  const handlePreviewDataReady = useCallback((data: DashboardPanel['data']) => {
    setLocalPanel(prev => {
      // formulas only exist on indicator/labeled_list data
      const prevFormulas = prev.data && 'formulas' in prev.data ? prev.data.formulas : undefined;
      if (prevFormulas !== undefined && data && 'formulas' in data) {
        return { ...prev, data: { ...data, formulas: prevFormulas } };
      }
      return { ...prev, data };
    });
  }, []);

  return (
    <div
      data-testid="panel-editor"
      className="panel-editor"
    >
      <div className="panel-editor__header">
        <h5 className="mb-0">{t('dashboard.panelEditor')}</h5>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            data-testid="panel-editor-cancel"
            onClick={onCancel}
          >
            {t('dashboard.cancel')}
          </button>
          <button
            className="btn btn-primary"
            data-testid="panel-editor-apply"
            disabled={!isDirty}
            onClick={() => onApply(localPanel)}
          >
            {t('dashboard.apply')}
          </button>
        </div>
      </div>

      <Group orientation="horizontal" className="panel-editor__body">
        <Panel defaultSize={60} minSize={30}>
          <Group orientation="vertical">
            <Panel defaultSize={50} minSize={20}>
              <div className="panel-editor__zone panel-editor__zone--preview">
                <EditorPreview panel={localPanel} contextKey={contextKey} layout={layout} onDataReady={handlePreviewDataReady} />
              </div>
            </Panel>
            <Separator className="panel-editor__handle panel-editor__handle--horizontal" />
            <Panel defaultSize={50} minSize={20}>
              <div className="panel-editor__zone panel-editor__zone--config">
                <EditorData panel={localPanel} onFormulasChange={handleFormulasChange} sourceKey={sourceKey} />
              </div>
            </Panel>
          </Group>
        </Panel>
        <Separator className="panel-editor__handle panel-editor__handle--vertical" />
        <Panel defaultSize={40} minSize={20}>
          <div className="panel-editor__zone panel-editor__zone--settings">
            <EditorSettings panel={localPanel} onConfigChange={handleConfigChange} onTitleChange={handleTitleChange} />
          </div>
        </Panel>
      </Group>
    </div>
  );
}
